import Phaser from 'phaser';
import {
  getGeodeMaxHp,
  hpToVisual,
} from '../../geode';
import { consumeEnergy, ENERGY_PER_TAP } from '../../player-state';
import { emitGame, GameEvents, onGame, offGame } from '../../events';
import {
  isBackendEnabled,
  applyOptimisticGeodeTap,
  queueSync,
  hasServerGeode,
  refreshProfileFromServer,
} from '../../../net/backend';
import { ensureLocalGeodeEntry, getGeodeSyncEntry, snapshotBreakLoot } from '../../server-state';
import type { GeodeModel } from '../types';
import { geodeLayoutFromModel } from './geode-model';
import { isPointerOnGeode } from './geode-hit-test';

type TapGuardFn = (pointer: Phaser.Input.Pointer) => boolean;

export type GeodeControllerCallbacks = {
  onCrackFrame: (frame: number, frameAdvanced: boolean) => void;
  onHitReaction: (pointer: Phaser.Input.Pointer) => void;
  onHitParticles: (x: number, y: number, opts?: { heavy?: boolean }) => void;
  onBreak: () => void;
  onRespawnStart: () => void;
  startRespawnTween: () => void;
  onRespawnComplete: () => void;
  isVisible: () => boolean;
  isRespawning: () => boolean;
};

export function createGeodeController(
  scene: Phaser.Scene,
  getModel: () => GeodeModel,
  sprite: Phaser.GameObjects.Image,
  callbacks: GeodeControllerCallbacks,
  tapGuard?: TapGuardFn,
) {
  let hp = isBackendEnabled()
    ? (getGeodeSyncEntry()?.hp ?? getGeodeMaxHp(getModel().type))
    : getGeodeMaxHp(getModel().type);
  let lastCrackFrame = 1;
  let broken = false;
  let respawning = false;

  function geodeType() {
    return getModel().type;
  }

  function currentHp() {
    if (isBackendEnabled()) {
      return getGeodeSyncEntry()?.hp ?? hp;
    }
    return hp;
  }

  function setHp(next: number) {
    hp = next;
    if (isBackendEnabled()) {
      const entry = getGeodeSyncEntry();
      if (entry) entry.hp = next;
    }
  }

  function applyCrackFrame(frame: number) {
    const frameAdvanced = frame > lastCrackFrame;
    callbacks.onCrackFrame(frame, frameAdvanced);
    lastCrackFrame = frame;
  }

  function syncVisual() {
    const visualHp = currentHp();
    const { frame } = hpToVisual(visualHp, geodeType());
    applyCrackFrame(frame);
  }

  function onTap(pointer: Phaser.Input.Pointer) {
    if (broken || respawning || !callbacks.isVisible()) return;

    const layout = geodeLayoutFromModel(getModel());
    if (
      !isPointerOnGeode(
        scene,
        sprite,
        pointer,
        { cx: layout.cx, cy: layout.cy, radius: layout.hitRadius },
        {
          blockedBy: tapGuard,
        },
      )
    ) {
      return;
    }

    if (!consumeEnergy(ENERGY_PER_TAP)) return;

    if (isBackendEnabled() && !hasServerGeode()) {
      void refreshProfileFromServer();
      return;
    }

    let nextHp: number;
    if (isBackendEnabled()) {
      nextHp = applyOptimisticGeodeTap();
    } else {
      const local = ensureLocalGeodeEntry();
      nextHp = Math.max(0, local.hp - 1);
      local.hp = nextHp;
    }

    const { frame } = hpToVisual(nextHp, geodeType());
    const frameAdvanced = frame > lastCrackFrame;
    applyCrackFrame(frame);

    callbacks.onHitReaction(pointer);
    callbacks.onHitParticles(pointer.worldX, pointer.worldY, { heavy: frameAdvanced });
    emitGame(GameEvents.GEODE_HIT, { x: pointer.worldX, y: pointer.worldY });

    if (nextHp <= 0) {
      broken = true;
      callbacks.onHitParticles(layout.cx, layout.cy, { heavy: true });
      snapshotBreakLoot();
      if (isBackendEnabled()) {
        queueSync('light-immediate');
      }
      emitGame(GameEvents.GEODE_BREAK);
      callbacks.onBreak();
    }
  }

  function respawn() {
    respawning = true;
    callbacks.onRespawnStart();
    const maxHp = getGeodeMaxHp(geodeType());
    setHp(isBackendEnabled() ? (getGeodeSyncEntry()?.hp ?? maxHp) : maxHp);
    lastCrackFrame = 1;
    broken = false;
    callbacks.startRespawnTween();
    emitGame(GameEvents.GEODE_RESPAWN, {
      cx: getModel().cx,
      cy: getModel().cy,
    });
  }

  function finishRespawn() {
    respawning = false;
    callbacks.onRespawnComplete();
  }

  const onGeodeSync = (payload: unknown) => {
    const { hp: syncedHp, broke } = (payload ?? {}) as { hp?: number; broke?: boolean };
    if (syncedHp == null) return;
    setHp(syncedHp);
    if (broke && !broken) {
      broken = true;
      respawning = false;
      callbacks.onBreak();
    } else if (!broke && !respawning && !broken) {
      syncVisual();
    } else if (!broke && respawning) {
      const { frame } = hpToVisual(syncedHp, geodeType());
      applyCrackFrame(frame);
    }
  };

  onGame(GameEvents.GEODE_SYNC, onGeodeSync);
  syncVisual();

  return {
    onTap,
    respawn,
    finishRespawn,
    syncVisual,
    isBroken: () => broken,
    isRespawning: () => respawning,
    setBroken(value: boolean) {
      broken = value;
    },
    destroy() {
      offGame(GameEvents.GEODE_SYNC, onGeodeSync);
    },
  };
}

export type GeodeController = ReturnType<typeof createGeodeController>;
